import { RelatedList, RelationListLayout, DataGridLayout } from 'framework/list';
import { useShowContext } from 'framework/show';
import { Button } from 'framework/ui/mui-builder';
import { Modal } from 'framework/ui/ui-builder';
import AddIcon from '@mui/icons-material/Add';
import React, { useState } from 'react';
import { TogglerInputLayout } from 'framework/ui/layout';
import { DateField, EmailField, FormattedTextField, TextField } from 'framework/ui/field';
import { Stack, Typography } from '@mui/material';
import { DATE_FORMAT, useDateService } from 'framework/utils';
import { CLIENT_PORTAL_USER_STATUS_LABELS } from 'domain/shared/clientPortal';
import { CLIENT_PORTAL_DISABLE_CONFIRMATION_MESSAGE, CLIENT_PORTAL_ENABLE_CONFIRMATION_MESSAGE } from './constants';
import ClientPortalActivateUser from './ClientPortalActivateUser';
import ClientPortalInviteUserModal from './ClientPortalInviteUserModal';
import useAccountManagementService from '../useAccountManagementService';
import { useAuthorization } from 'framework/authorization';
import { DELIVERY_ACCOUNT_INVITE_CLIENT_PORTAL_USER } from '../model';

const RelatedClientPortalList = () => {
  const { data: accountData, setData } = useShowContext();
  const { formatDate, getToday } = useDateService();
  const account = accountData.getData();
  const [isPortalEnabled, setIsPortalEnabled] = useState(Boolean(account.isPortalEnabled));
  const [pendingPortalEnabled, setPendingPortalEnabled] = useState<boolean | null>(null);
  const [isPortalToggleSubmitting, setIsPortalToggleSubmitting] = useState(false);
  const [isInviteUserModalOpen, setIsInviteUserModalOpen] = useState(false);
  const [listReloadKey, setListReloadKey] = useState(0);
  const { isGranted } = useAuthorization();

  const { update, getClientPortalUsers } = useAccountManagementService();

  const closePortalConfirmation = () => setPendingPortalEnabled(null);
  const confirmPortalToggle = async () => {
    if (pendingPortalEnabled === null) {
      closePortalConfirmation();
      return;
    }
    setIsPortalToggleSubmitting(true);

    try {
      const updatedAccount = {
        ...account,
        isPortalEnabled: pendingPortalEnabled,
      };

      await update(account.accountId, updatedAccount);
      setData?.(updatedAccount);
      setIsPortalEnabled(pendingPortalEnabled);
      closePortalConfirmation();
    } finally {
      setIsPortalToggleSubmitting(false);
    }
  };

  const portalConfirmationMessage = pendingPortalEnabled
    ? CLIENT_PORTAL_ENABLE_CONFIRMATION_MESSAGE
    : CLIENT_PORTAL_DISABLE_CONFIRMATION_MESSAGE;

  return (
    <RelationListLayout
      header="Client portal"
      headerVariant="h4"
      actions={
        isPortalEnabled && isGranted(DELIVERY_ACCOUNT_INVITE_CLIENT_PORTAL_USER)
          ? [
              <Button onClick={() => setIsInviteUserModalOpen(true)} size="small">
                <AddIcon fontSize={'small'} />
                Invite users
              </Button>,
            ]
          : []
      }
    >
      <>
        <Stack
          sx={{
            display: 'flex',
            gap: 1,
            pb: 4,
            '.field__wrapper': { display: 'flex', alignItems: 'baseline' },
            '.field-label': { fontSize: 12, pr: 1 },
          }}
        >
          <Stack direction="row" alignItems="center" spacing={1}>
            <Typography noWrap overflow="visible" pr={1}>
              Portal is enabled for this account
            </Typography>
            <TogglerInputLayout
              source={'isPortalEnabled'}
              label=""
              checkedValue={'Yes'}
              uncheckedValue={'No'}
              value={isPortalEnabled}
              isControlledInput
              onChangeCallback={(values: object) => {
                setPendingPortalEnabled(Boolean((values as Dictionary<boolean>).isPortalEnabled));
              }}
            />
          </Stack>
          {isPortalEnabled && (
            <FormattedTextField
              source={'portalEnabledDate'}
              format={(str: string) => formatDate(str || getToday(), DATE_FORMAT.FULL_DATE)}
              label="Enabled on"
            />
          )}
        </Stack>
        {isPortalEnabled && (
          <RelatedList key={listReloadKey} getDataMethod={() => getClientPortalUsers(account.accountId)}>
            <DataGridLayout isSmallTable showTotal={false}>
              <TextField width="20%" source="name" label="Name" />
              <EmailField width="25%" source="email" label="Email" />
              <FormattedTextField
                width="10%"
                format={(status: string) => CLIENT_PORTAL_USER_STATUS_LABELS[status]}
                source="status"
                label="Status"
              />
              <DateField width="15%" source="invitedAt" label="Invited at" />
              <DateField width="15%" source="lastLoginAt" label="Last login" />
              <DateField width="15%" source="accessUntil" label="Access until" />
              {isGranted(DELIVERY_ACCOUNT_INVITE_CLIENT_PORTAL_USER) && (
                <ClientPortalActivateUser
                  statusSource={'status'}
                  account={account}
                  userIdSource={'id'}
                  onSubmitSuccess={() => setListReloadKey(prev => prev + 1)}
                />
              )}
            </DataGridLayout>
          </RelatedList>
        )}
        <Modal
          isOpen={pendingPortalEnabled !== null}
          title="Are you sure?"
          onToggle={closePortalConfirmation}
          buttons={[
            <Button key="confirm-portal-toggle" onClick={confirmPortalToggle} loading={isPortalToggleSubmitting}>
              Confirm
            </Button>,
          ]}
        >
          {portalConfirmationMessage}
        </Modal>
        <ClientPortalInviteUserModal
          accountAlias={account.accountId}
          defaultAccessUntil={account.budgetEndDate}
          isOpen={isInviteUserModalOpen}
          onClose={() => setIsInviteUserModalOpen(false)}
          onInvited={() => setListReloadKey(prev => prev + 1)}
        />
      </>
    </RelationListLayout>
  );
};

export default RelatedClientPortalList;
